import { useState, useEffect, useMemo, useRef, MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'store/hooks'
import { useAuth, useDidMountEffect } from 'hooks'
import { useCertificateFilters } from 'hooks/useCertificateFilters'
import { useCertificateTabs } from 'hooks/useCertificateTabs'
import { useCertificateSelection } from 'hooks/useCertificateSelection'
import { useCertificateUI } from 'hooks/useCertificateUI'
import { accountLinkCheck } from 'store/auth/actions'
import { getTabsSummary } from 'store/certificates/actions'
import { DEFAULT_FILTERS, DEFAULT_LAYOUT, DISPLAYED_DATA_DEFAULTS } from 'constants/certificateConstants'
import type { CerteficateTab, Sort } from 'types'
import { useGetCertificates } from 'hooks/api/useGetCertificates'
import { ROUTES } from 'routes'
import { helpNavState } from 'pages/Help/navState'
import { getLoadedPages, saveLoadedPages } from 'utils/scrollRestore'
import type { OwnershipFilterValue } from 'constants/certificateConstants'

const PAGE_SIZE = 20

/**
 * The key both the scroll position and the loaded-page count are stored under.
 *
 * One constant because they MUST agree: a scroll offset restored against the
 * wrong page count scrolls a document shorter than the offset, the browser
 * clamps it, and the restore silently does nothing.
 */
const LIST_KEY = '/certificates'

export type CertificatesFilters = {
  limit: number;
  offset: number;
  tab: CerteficateTab;
  status: string | null;
  type: string | null;
  sort: Sort | null;
  sort_by: string | null;
  query: string | null;
  certificateIds: string | null;
  /**
   * VRG-511 — which certificates count as mine. Sent verbatim as the `ownership`
   * query param; the API defaults it to `current` too, so the two cannot drift
   * into disagreeing about what an absent value means.
   */
  ownership: OwnershipFilterValue;
};

export const useCertificatesPageState = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Core state
  const [filters, setFilters] = useState<CertificatesFilters>(DEFAULT_FILTERS)
  const [tab, setTab] = useState<CerteficateTab>(DEFAULT_FILTERS.tab)
  const [linkAccounts, setLinkAccounts] = useState<boolean>(true)
  const [mobileAddSort, setMobileAddSort] = useState<boolean>(false)
  const [isFilterChanged, setIsFilterChanged] = useState<boolean>(false)
  const [isLogOutOpen, setIsLogOutOpen] = useState(false)
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [allCertificates, setAllCertificates] = useState<any[]>([])
  const [selectedCertificateIds, setSelectedCertificateIds] = useState<string[]>([])

  // Anchor states for popovers/menus
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null)
  const [sortConfigAnchor, setSortConfigAnchor] = useState<HTMLElement | null>(null)
  const [moreOptionsAnchor, setMoreOptionsAnchor] = useState<HTMLElement | null>(null)
  const [displayedDataAnchor, setDisplayedDataAnchor] = useState<HTMLElement | null>(null)
  const sortButtonRef = useRef<HTMLButtonElement | null>(null)

  // Displayed data with localStorage persistence
  const [displayedData, setDisplayedData] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('certificates.displayedData')
      if (raw) return JSON.parse(raw)
    } catch {}
    return DISPLAYED_DATA_DEFAULTS
  })

  /**
   * List vs grid, persisted.
   *
   * Reuses the `vera_certificates_layout` key a previous iteration left behind
   * in localStorage — same feature, same meaning, so honouring it means anyone
   * who set a preference before still has it.
   *
   * Defaults to LIST, and that default is measured rather than assumed: when
   * grid was briefly the only layout it fitted 2 cards above the fold against
   * list's 3, with row-height deltas up to 199px because each card's activity
   * block is a different height. As a USER CHOICE that trade is theirs to make;
   * as a default it was just worse.
   */
  const [layout, setLayout] = useState<'list' | 'grid'>(() => {
    try {
      const raw = localStorage.getItem('vera_certificates_layout')
      if (raw === 'grid' || raw === 'list') return raw
    } catch {}
    // `DEFAULT_LAYOUT`, not a second literal `'list'`: the Display popover's
    // Reset restores the same constant, so the default and what Reset returns
    // you to cannot drift apart.
    return DEFAULT_LAYOUT
  })

  // Auth and data hooks
  const { isAuthenticated } = useAuth()
  const { shopify_user, linkedAccounts, tabsSummary } = useSelector(state => ({
    shopify_user: state.auth.shopify_user,
    linkedAccounts: state.auth.isAccountLinked,
    tabsSummary: state.certificates.tabsSummary,
  }))

  // UI state hook
  const { uiState, sortState, setOpenSort, setOpenFilter, setOpenMore, setOpenSelect, toggleSelect, setOpenDisplayedData, setMobileSortStep, setMobileSortField, setMobileSortDir, setAdditionalSorts } = useCertificateUI()

  /*
    INFINITE SCROLL, expressed as a growing LIMIT rather than a moving offset.

    The obvious shape is `useInfiniteQuery` with page-at-a-time accumulation.
    This is one query asking for `pages * PAGE_SIZE` rows from offset 0, and it
    is the better fit here for two reasons the accumulating version gets wrong:

    - This list POLLS every 20s (see `useGetCertificates`). An accumulator
      refreshes only the page the poll asked for, so rows 1-20 stay current
      while 21-60 quietly rot. Re-asking for the whole loaded window keeps every
      row on screen as fresh as every other — which matters on a list whose
      point is to surface an incoming transfer.
    - There is no merge step, so there is no way to double-render a row that
      moved between pages while you were reading, and a filter change needs no
      cache reset — the key simply changes.

    The cost, stated: loading page 4 re-fetches 80 rows rather than 20. At this
    page size and the sizes a personal collection runs to that is a few KB, and
    it buys the two properties above. If a collection ever makes that the wrong
    trade, the fix is a real `useInfiniteQuery`, not a bigger PAGE_SIZE.
  */
  const [loadedPages, setLoadedPages] = useState(() => getLoadedPages(LIST_KEY) ?? 1)

  const query = useMemo(
    () => ({ ...filters, limit: loadedPages * PAGE_SIZE, offset: 0 }),
    [filters, loadedPages]
  )

  // Data hooks
  const { data, isLoading, isFetching, isRefetching, isPlaceholderData, isError, error, refetch } =
    useGetCertificates(query);
  const certificates = data?.data;

  const total = data?.total ?? 0
  const loadedCount = certificates?.length ?? 0

  /*
    More rows are LEFT TO LOAD, which is not the same question as the pager's
    "there is a next page". Compared against what is on screen rather than
    against a page number, so it cannot disagree with the list the user is
    looking at.
  */
  const isHasMorePages = loadedCount < total

  const loadMore = () => {
    if (isFetching || !isHasMorePages) return
    setLoadedPages(pages => pages + 1)
  }

  /*
    Any change that redefines the RESULT SET collapses the window back to one
    page. The six call sites that each paired a `setPage(1)` with `offset: 0`
    are gone in favour of this: a seventh filter added later gets the reset for
    free, where before it would have been one more place to remember.

    `useDidMountEffect`, not `useEffect`: on mount this would clobber a page
    count restored from a back-navigation with 1, which is precisely the restore
    it exists to protect.
  */
  useDidMountEffect(() => {
    setLoadedPages(1)
  }, [
    filters.tab,
    filters.status,
    filters.type,
    filters.sort,
    filters.sort_by,
    filters.query,
    filters.certificateIds,
    filters.ownership,
  ])

  /* Kept current rather than written on the way out: the card's own click
     handler saves the scroll position, and a second thing to remember there is
     a second thing to forget. */
  useEffect(() => {
    saveLoadedPages(LIST_KEY, loadedPages)
  }, [loadedPages])

  const { selectedIds, selectMode, isSelected, toggleSelected, selectAll, clearSelection } = useCertificateSelection({ certificates: certificates || [] })

  const { baseFilteredCertificates, sortedCertificates, visibleCertificates, hasActiveFilters } = useCertificateFilters({
    certificates,
    filters,
    additionalSorts: sortState.additionalSorts,
    tab,
  })

  const [filtersApplied, setFiltersApplied] = useState(false)
  
  const hasActiveFiltersOrSearch = Boolean(
    filters.certificateIds ||
    filters.status ||
    filters.type ||
    filters.query
  )

  const { tabsSummaryList, getTabCount } = useCertificateTabs({
    baseCertificates: baseFilteredCertificates || [],
    originalCertificates: certificates || [],
    isRefetching,
    isLoading,
    hasActiveFilters: hasActiveFiltersOrSearch && filtersApplied, // Only update when filters are actually applied
  })

  const isEmpty = visibleCertificates?.length === 0

  // Effects
  // Handlers refetch
  // useDidMountEffect(() => {
  //   if (filtersApplied) {
  //     refetch(filters)
  //   }
  // }, [filters, filtersApplied])

  useEffect(() => {
    if (certificates && !isFilterChanged && tab === 'all') {
      setAllCertificates(certificates)
    }
  }, [certificates, isFilterChanged, tab])

  useEffect(() => {
    if (isAuthenticated && shopify_user) {
      dispatch(accountLinkCheck({ email: shopify_user }, () => {}))
    }
  }, [isAuthenticated, shopify_user])

  useEffect(() => {
    setLinkAccounts(linkedAccounts)
  }, [linkedAccounts])

  // Reset filtersApplied when filters are cleared
  useEffect(() => {
    if (!hasActiveFiltersOrSearch) {
      setFiltersApplied(false)
    }
  }, [hasActiveFiltersOrSearch])

  // Event handlers
  const onFiltersChange = (
    statuses: string | null,
    types: string | null,
    checkedItems: any[],
    certificateIds?: string,
    activityFrom?: string | null,
    activityTo?: string | null
  ) => {
    const newFilters = {
      ...filters,
      ...(statuses ? { status: statuses } : { status: null }),
      ...(types ? { type: types } : { type: null }),
      ...(certificateIds ? { certificateIds: certificateIds } : { certificateIds: null }),
      ...(activityFrom ? { activityFrom } : { activityFrom: undefined }),
      ...(activityTo ? { activityTo } : { activityTo: undefined }),
    }

    setFilters(newFilters)
    /*
     * COMMITTING NO LONGER CLOSES THE DRAWER.
     *
     * `setOpenFilter(false)` lived here, and under the Apply model it was right:
     * pressing Apply meant "I am done", so committing and dismissing were one
     * gesture. Under auto-apply they are not — this runs 400ms after any pill,
     * date or certificate is touched, so leaving the close in place dismissed the
     * sheet the moment the user picked their FIRST filter. Measured on the mobile
     * sheet at 375: tap Status = Stolen, and 400ms later the sheet vanished.
     *
     * This was the half of "auto-apply" that does not live in the sheet: the sheet
     * dropped its own `onClose()` from the commit path, and the page's handler
     * still had one. Both had to go.
     */
    setIsFilterChanged(true)
    setFilteredItems(checkedItems)
    setFiltersApplied(true)
  }

  /**
   * Clear EVERY applied filter in one gesture — the Filter control's welded X.
   *
   * Routed through `onFiltersChange` with every axis null rather than resetting
   * `filters` directly, so the clear path and the apply path are the same code:
   * the drawer's own Reset already commits by calling this handler with empty
   * values, and a second, independent "set the filters back to nothing" is
   * exactly where the two would drift (one forgetting `activityFrom`, or
   * `filteredItems`, or the page reset).
   *
   * `selectedCertificateIds` is cleared too — it feeds `filterBadgeCount`, so
   * leaving it would clear the pills while the badge still showed a number.
   */
  const onFiltersClear = () => {
    setSelectedCertificateIds([])
    /*
     * ⚠️ ORDER IS LOAD-BEARING, and it is not obvious.
     *
     * Both of these write `filters`, but they write it DIFFERENTLY:
     * `onFiltersChange` does `setFilters(newFilters)` with an object built from
     * the `filters` it closed over, while `onStatusesChange` does
     * `setFilters(state => …)`. React batches the pair, so a functional update
     * sees the object update's result, but never the reverse.
     *
     * Called statuses-first, the second call therefore reinstated the very
     * statuses the first had just cleared — from its stale snapshot — and the X
     * did nothing at all. MEASURED: badge stayed at 1, list stayed narrowed.
     *
     * So the object write goes first and the functional one last. (The durable
     * fix is for `onFiltersChange` to be functional too; that is a wider change
     * to a handler with six callers, so it is not made from here.)
     *
     * Statuses need clearing at all because the pills commit down their own path
     * — otherwise the X would clear everything the badge counts EXCEPT the axis
     * that most often put a number there.
     */
    onFiltersChange(null, null, [], undefined, null, null)
    onStatusesChange([])
    // VRG-511 — back to the list's default meaning. Omitting this would leave the
    // X clearing every axis except the one that changes which rows exist, so a
    // "cleared" filter could still be hiding everything the user owns.
    onOwnershipChange('current')
  }

  const onSortChange = (sort_by: string | null, sort: Sort | null) => {
    setFilters((state: any) => ({ ...state, offset: 0, sort_by, sort }))
    /*
     * Same removal, same reason (`setOpenSort(false)` was here). Note the blast
     * radius is mobile-only either way: `uiState.openSort` gates the mobile
     * `SortDrawer`, while the desktop sort menu is anchored on `sortMenuAnchor` and
     * closes itself — so the desktop call sites were always passing through a
     * no-op here.
     */
  }

  const onSearchChange = (value: string) => {
    const normalized = value.trim()
    const newFilters = { ...filters, offset: 0, query: normalized.length ? normalized : null }
    
    setFilters(newFilters)
    
    if (normalized.length > 0) {
      setFiltersApplied(true)
      dispatch(getTabsSummary({ query: normalized }))
    } else {
      setFiltersApplied(false)
      dispatch(getTabsSummary({ query: null }))
    }
  }

  const handleTabsChange = (tab: CerteficateTab) => {
    setFilters((state: any) => ({ ...state, offset: 0, tab }))
  }

  const handleChangeIndex = (tab: CerteficateTab) => {
    setTab(tab)
    setFilters((state: any) => ({ ...state, offset: 0, tab }))
  }

  const handleFolderClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = (menuItem: string) => {
    performActionBasedOnMenuItem(menuItem)
    setAnchorEl(null)
  }

  const performActionBasedOnMenuItem = (menuItem: string) => {
    if (menuItem === 'Profile') {
      navigate('/profile')
    }

    if (menuItem === 'Notifications') {
      navigate('/notifications')
    }

    if (menuItem === 'Transfer history') {
      // TODO: Implement transfer history navigation
      console.log('Navigate to transfer history')
    }

    // VRG-418: was a `console.log` TODO, which is why the row was hidden. The route is
    // real now. It carries `helpNavState` for the same reason every other entry point
    // does — Help is reached from three places and cannot name its own back-link.
    if (menuItem === 'Help') {
      navigate(ROUTES.HELP, {
        state: helpNavState('My certificates', ROUTES.CERTIFICATES),
      })
    }

    if (menuItem === 'Log out') {
      setIsLogOutOpen(true)
    }
  }

  // Computed values
  /**
   * How many filters are applied — the number in the Filter control's pill, and
   * the thing that decides whether its clear-X is out.
   *
   * ⚠️ STATUS WAS MISSING, and status is the filter people actually use. The
   * pills commit down their own path (`onStatusesChange` -> `filters.statuses`),
   * separate from the `checkedItems` array the other controls feed, and this
   * count only ever summed the latter. So the observable behaviour was: pick
   * "Lost", the list correctly narrows to the lost certificates, and the toolbar
   * goes on saying plain "Filter" with no count and nothing to clear —
   * MEASURED live before the fix (list 4 -> 2 items, badge absent).
   *
   * `statuses.length` is the honest count rather than a flat 1 because each pill
   * is an independent facet, matching how `selectedCertificateIds` is counted.
   * An EMPTY array is the "All" sentinel (`CertificatesFilter` renders
   * `localStatuses.length === 0` as the All pill), so unfiltered correctly
   * contributes 0 and no `|| 0` special-casing is needed.
   *
   * The axes are summed rather than counted as "how many kinds of filter are on"
   * because the pill sits inside a control the user is about to open: it should
   * say how much is going to be there, not how many sections are non-empty.
   */
  const filterBadgeCount =
    (filteredItems.length +
      selectedCertificateIds.length +
      ((filters as any).statuses?.length ?? 0) +
      ((filters as any).activityFrom || (filters as any).activityTo ? 1 : 0) +
      // VRG-511 — counts only when it is NOT the default. The badge answers "how
      // much is filtered", and the list's ordinary meaning is not a filter.
      ((filters as any).ownership && (filters as any).ownership !== 'current' ? 1 : 0)) || 0
  const sortBadgeCount = (filters.sort_by ? 1 : 0) + sortState.additionalSorts.length
  const showSortClear = ((filters.sort_by ? 1 : 0) + sortState.additionalSorts.length) > 0

  // Sort handlers
  const handleSortClick = (e: React.MouseEvent<HTMLButtonElement>, isMobile: boolean) => {
    if (isMobile) {
      setMobileSortStep(filters.sort_by ? 'configure' : 'pick')
      setMobileAddSort(false)
      setOpenSort(true)
    } else {
      if (filters.sort_by || sortState.additionalSorts.length) {
        setSortConfigAnchor(sortButtonRef.current)
      } else {
        setSortMenuAnchor(e.currentTarget as HTMLElement)
      }
    }
  }

  const handleSortClear = () => {
    onSortChange(null, null)
    setAdditionalSorts([])
  }

  const handleMoreOptionsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMoreOptionsAnchor(e.currentTarget)
  }


  // Commit point for the status pills. The sheet holds its own copy while you tap
  // and calls this on the shared 400ms debounce — so tapping three pills in a row
  // lands here once, not three times. It does not close the sheet (see
  // `onFiltersChange`).
  const onStatusesChange = (statuses: string[]) => {
    setFilters((state: any) => ({ ...state, offset: 0, statuses }))
  }

  /*
   * VRG-511 — commit point for the ownership scope.
   *
   * The window reset is not boilerplate here, and it is now handled by the
   * shared `useDidMountEffect` above rather than at this call site. This is the
   * one filter that changes the SIZE of the result set on the server, so
   * carrying a four-page window into a scope with two rows in it would ask for
   * 80 rows, get 2, and read as "you have no previously-owned items" — the exact
   * wrong conclusion.
   */
  const onOwnershipChange = (ownership: OwnershipFilterValue) => {
    setFilters((state: any) => ({ ...state, offset: 0, ownership }))
  }

  const handleLayoutChange = (next: 'list' | 'grid') => {
    setLayout(next)
    try { localStorage.setItem('vera_certificates_layout', next) } catch {}
  }

  // Displayed data handlers
  const handleDisplayedDataChange = (key: string, checked: boolean) => {
    setDisplayedData(prev => {
      const next = { ...prev, [key]: checked }
      try { 
        localStorage.setItem('certificates.displayedData', JSON.stringify(next)) 
      } catch {}
      return next
    })
  }

  return {
    // State
    filters,
    tab,
    linkAccounts,
    mobileAddSort,
    isFilterChanged,
    isLogOutOpen,
    filteredItems,
    allCertificates,
    selectedCertificateIds,
    anchorEl,
    sortMenuAnchor,
    sortConfigAnchor,
    moreOptionsAnchor,
    displayedDataAnchor,
    sortButtonRef,
    displayedData,
    layout,
    
    // UI State
    uiState,
    sortState,
    
    // Data
    certificates,
    visibleCertificates,
    tabsSummaryList,
    isEmpty,
    total,
    /* The size of the loaded window, in pages — and `loadMore`, which grows it.
       There is no `page`: nothing can jump to an arbitrary one any more. */
    loadedPages,
    loadMore,
    isHasMorePages,
    isLoading,
    /* Any request in flight, INCLUDING a load-more. `isRefetching` is false for
       the first fetch of a new key, which is exactly what a load-more is, so the
       sentinel would fire again under its own request. */
    isFetching,
    isRefetching,
    /* True only while the rows on screen belong to the PREVIOUS filter/sort key —
       never during a same-key background poll. Drives the grid's swap transition;
       see `placeholderData` in `useGetCertificates`. */
    isPlaceholderData,
    isError,
    error,
    
    // Selection
    selectedIds,
    selectMode,
    isSelected,
    toggleSelected,
    selectAll,
    clearSelection,
    
    // Computed
    filterBadgeCount,
    sortBadgeCount,
    showSortClear,
    
    // Setters
    setFilters,
    setTab,
    setLinkAccounts,
    setMobileAddSort,
    setIsFilterChanged,
    setIsLogOutOpen,
    setFilteredItems,
    setAllCertificates,
    setSelectedCertificateIds,
    setAnchorEl,
    setSortMenuAnchor,
    setSortConfigAnchor,
    setMoreOptionsAnchor,
    setDisplayedDataAnchor,
    setOpenSort,
    setOpenFilter,
    setOpenMore,
    setOpenSelect,
    toggleSelect,
    setOpenDisplayedData,
    setMobileSortStep,
    setMobileSortField,
    setMobileSortDir,
    setAdditionalSorts,
    
    // Handlers
    onFiltersChange,
    onFiltersClear,
    onSortChange,
    onSearchChange,
    handleTabsChange,
    handleChangeIndex,
    handleFolderClick,
    handleMenuItemClick,
    performActionBasedOnMenuItem,
    handleSortClick,
    handleSortClear,
    handleMoreOptionsClick,
    handleDisplayedDataChange,
    handleLayoutChange,
    onStatusesChange,
    onOwnershipChange,

    // Actions
    refetch,
  }
}
